import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { AuthModule } from '@auth0/auth0-angular';
import { NG_ENTITY_SERVICE_CONFIG } from '@datorama/akita-ng-entity-service';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { BreadcrumbsModule } from '@molecules/breadcrumbs/breadcrumbs.module';
import { ContentSectionModule } from '@molecules/content-section/content-section.module';
import { ListModule } from '@molecules/list/list.module';
import { PolicyBreakdownModule } from '@molecules/policy-breakdown/policy-breakdown.module';
import { SidebarInfocardModule } from '@molecules/sidebar-infocard/sidebar-infocard.module';
import { PetInformationComponent } from '@pages/pet-information/pet-information.component';
import * as Sentry from '@sentry/angular-ivy';
import { KameleoonService } from '@services/kameleoon.service';
import { SharedModule } from '@shared/shared.module';
import { STATSIG_INIT_CONFIG, StatsigModule } from '@statsig/angular-bindings';
import { UiCoreModule } from '@ui-core/ui-core.module';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorHandlerService } from './error-handler.service';
import { CallbackComponent } from './pages/callback/callback.component';
import { HomeComponent } from './pages/home/home.component';
import { RetrieveComponent } from './pages/retrieve/retrieve.component';
import { AgeSelectMenuModule } from './shared/age-select-menu/age-select-menu.module';
import { AutoSearchModule } from './shared/autosearch/autosearch.module';

const StatsigConfig = {
	sdkKey: environment.statsig.clientKey,
	user: {
		id: undefined
	},
	options: {
		environment: { tier: environment.statsig.environment }
	}
};

@NgModule({
	declarations: [
		AppComponent,
		PetInformationComponent,
		CallbackComponent,
		HomeComponent,
		RetrieveComponent
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		HttpClientModule,
		AppRoutingModule,
		UiCoreModule,
		BreadcrumbsModule,
		ContentSectionModule,
		SidebarInfocardModule,
		PolicyBreakdownModule,
		FormsModule,
		ListModule,
		SharedModule,
		AgeSelectMenuModule,
		AutoSearchModule,
		ReactiveFormsModule,
		RecaptchaV3Module,
		StatsigModule,
		environment.production ? [] : AkitaNgDevtools.forRoot(),
		AuthModule.forRoot({
			domain: environment?.USAAAuth?.domain, // 'embracepetinsurance-dev.auth0.com',
			clientId: environment?.USAAAuth?.clientID, // 'P55o7CnZ7qQOTQKrCu7fIhI3C01h0dYk',
			redirectUri: `${location.origin}/callback`,
			useRefreshTokens: true
		})
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	providers: [
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: true
			})
		},
		{
			provide: Sentry.TraceService,
			deps: [Router]
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true
		},
		{
			provide: NG_ENTITY_SERVICE_CONFIG,
			useValue: { baseUrl: 'https://jsonplaceholder.typicode.com' }
		},
		{ provide: ErrorHandler, useClass: ErrorHandlerService },
		{
			provide: RECAPTCHA_V3_SITE_KEY,
			useValue: environment.recaptcha.siteKey,
		},
		KameleoonService,
		{
			provide: STATSIG_INIT_CONFIG,
			useValue: StatsigConfig
		}
	]
})
export class AppModule {}
